import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useState } from "react";

import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { Contact, getContactSubtitle, getContactTitle, isCallableContact } from "../internal-contacts";
import { useStartInternalTwilioPersonCall } from "../internal-twilio-call/hooks/useStartInternalTwilioPersonCall";
import { useContactCenterTheme } from "../virtual-receptionist/theming/ContactCenterThemeContext";

interface MessageSearchControls {
  searchOpen: boolean;
  searchText: string;
  inputRef: React.RefObject<HTMLInputElement>;
  open: () => void;
  close: () => void;
  clear: () => void;
  setSearchText: (value: string) => void;
}

interface Props {
  contact: Contact;
  search?: MessageSearchControls;
}

export function InternalChatHeader({ contact, search }: Props) {
  const { theme } = useContactCenterTheme();
  const startTwilioPersonCall = useStartInternalTwilioPersonCall();
  const { openCalls } = useFloatingBubbles();
  const [isStartingCall, setIsStartingCall] = useState(false);

  const title = getContactTitle(contact);
  const subtitle = getContactSubtitle(contact);
  const canCall = isCallableContact(contact);

  const handleStartCall = useCallback(async () => {
    if (!canCall || isStartingCall) return;

    setIsStartingCall(true);
    try {
      const started = await startTwilioPersonCall(contact, "InternalChatHeader");
      if (started) openCalls();
    } finally {
      setIsStartingCall(false);
    }
  }, [canCall, contact, isStartingCall, openCalls, startTwilioPersonCall]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1,
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: theme.shell.border,
        backgroundColor: theme.shell.headerBg,
        color: theme.shell.textPrimary,
        flexShrink: 0,
        minHeight: 48,
        gap: 0.5,
      }}
    >
      {search?.searchOpen ? (
        <>
          <IconButton size="small" aria-label="Close search" onClick={search.close} sx={{ color: theme.shell.textSecondary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <SearchIcon sx={{ fontSize: 18, color: theme.shell.textSecondary, flexShrink: 0 }} />

          <Box
            component="input"
            ref={search.inputRef}
            value={search.searchText}
            onChange={(event) => search.setSearchText(event.target.value)}
            placeholder="Search messages..."
            sx={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 13.5,
              color: theme.shell.textPrimary,
              "&::placeholder": { color: theme.shell.textSecondary },
            }}
          />

          {search.searchText && (
            <IconButton size="small" aria-label="Clear search" onClick={search.clear} sx={{ p: 0.25, color: theme.shell.textSecondary }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </>
      ) : (
        <>
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", px: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              fontSize: 11,
              color: theme.shell.textSecondary,
              lineHeight: 1.2,
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {canCall && (
        <IconButton
          size="small"
          aria-label="Call"
          onClick={() => void handleStartCall()}
          disabled={isStartingCall}
          sx={{
            color: theme.callButton.color,
            "&:hover": { backgroundColor: theme.callButton.hoverBg },
          }}
        >
          <PhoneIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {search && (
        <Tooltip title="Search messages">
          <IconButton size="small" aria-label="Search messages" onClick={search.open} sx={{ color: theme.shell.textSecondary }}>
            <SearchIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
        </>
      )}
    </Box>
  );
}
